import React, { useState, useMemo } from 'react'
import { Tooltip, IconButton } from '@mui/material'
import { Icon, CaseHistoryPopover } from 'components'

interface CaseHistoryProps {
    row: any
    rowIndex: number
    testId: string
    className?: string
    iconSize?: 'small' | 'medium' | 'large'
    tooltipTitle?: string
}

interface HistoryRecord {
    timestamp: string
    action: string
    user: string
}

const CaseHistory = ({
    row,
    rowIndex,
    testId,
    className = 'emp-caseMenuIcon',
    iconSize = 'small',
    tooltipTitle = 'Case History',
}: CaseHistoryProps): React.ReactElement => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const handleOpenHistory = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleCloseHistory = () => {
        setAnchorEl(null)
    }

    // Create a modified row object with mock history data for the CaseHistoryModal
    const caseInfoWithMockData = useMemo(() => {
        return {
            ...row,
            case_no: row.case_number || row.case_no || row.id,
            // The CaseHistoryModal will use this mock data instead of making API calls
            mockHistoryData: generateMockHistoryData(row)
        }
    }, [row])

    // Helper function to generate mock history data
    function generateMockHistoryData(rowData: any): HistoryRecord[] {
        const records: HistoryRecord[] = []
        
        // Helper function to parse date string safely (avoiding timezone issues)
        const parseDate = (dateString: string): Date => {
            if (!dateString) return new Date()
            
            // Parse YYYY-MM-DD format safely by creating date in local timezone
            const parts = dateString.split('-')
            if (parts.length === 3) {
                const year = parseInt(parts[0], 10)
                const month = parseInt(parts[1], 10) - 1 // Month is 0-indexed
                const day = parseInt(parts[2], 10)
                return new Date(year, month, day)
            }
            
            // Fallback to regular Date parsing
            return new Date(dateString)
        }
        
        // Helper function to format date with random time
        const formatDate = (date: Date, seedOffset: number = 0) => {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            
            // Generate deterministic but random-looking time based on date and seed
            const seed = date.getTime() + seedOffset
            const hours = String(Math.floor((seed % 86400) / 3600) % 24).padStart(2, '0')
            const minutes = String(Math.floor((seed % 3600) / 60) % 60).padStart(2, '0')
            const seconds = String(seed % 60).padStart(2, '0')
            
            return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`
        }

        // Helper function to add/subtract days from a date
        const addDays = (date: Date, days: number) => {
            const result = new Date(date)
            result.setDate(result.getDate() + days)
            return result
        }

        // Collect all case cycles (master + details) for complete lifecycle
        const allCycles: any[] = []
        
        // Add master record (current/latest cycle)
        allCycles.push({
            ...rowData,
            isMaster: true,
            cycleOrder: 0 // Latest cycle
        })
        
        // Add detail records (historical cycles) if they exist
        if (rowData.details_data && Array.isArray(rowData.details_data)) {
            rowData.details_data.forEach((detail: any, index: number) => {
                allCycles.push({
                    ...detail,
                    isMaster: false,
                    cycleOrder: index + 1 // Historical cycles
                })
            })
        }
        
        // Sort cycles chronologically (earliest first for processing)
        allCycles.sort((a, b) => {
            const dateA = new Date(a.creation_date || a.date_created)
            const dateB = new Date(b.creation_date || b.date_created)
            return dateA.getTime() - dateB.getTime()
        })

        // Process each cycle to generate history records
        allCycles.forEach((cycle, cycleIndex) => {
            // For escalated cases (not the first cycle), use the due date of the previous cycle
            let creationDate: Date
            if (cycleIndex > 0 && allCycles[cycleIndex - 1]?.due_date) {
                // This is an escalated case - use the due date of the previous cycle (when escalation happened)
                creationDate = parseDate(allCycles[cycleIndex - 1].due_date)
            } else {
                // First cycle or no previous due date - use actual creation date
                creationDate = cycle.creation_date ? parseDate(cycle.creation_date) : 
                              cycle.date_created ? parseDate(cycle.date_created) : new Date()
            }
            
            const completedDate = cycle.date_completed ? parseDate(cycle.date_completed) : null
            const transDate = cycle.trans_date ? parseDate(cycle.trans_date) : 
                             cycle.date_trans ? parseDate(cycle.date_trans) : null
            const dueDate = cycle.due_date ? parseDate(cycle.due_date) : null
            
            // For the very first cycle, add transaction processing record if trans_date exists
            if (cycleIndex === 0 && transDate && transDate < creationDate) {
                records.push({
                    timestamp: formatDate(transDate, 1000),
                    action: `Transaction processed - Merchant ${cycle.merchant_name || 'Unknown Merchant'} processed Amount: ${cycle.currency || 'USD'} ${cycle.amount || '0.00'} ${cycle.account ? ` (Account: ${cycle.account})` : ''}`,
                    user: 'Merchant'
                })
            }
            
            // Case creation/cycle initiation record
            let cycleAction = ''
            if (cycleIndex === 0) {
                // First cycle chronologically - original case creation with merchant and amount details
                cycleAction = `Case created - ${cycle.type || 'Case'} initiated for merchant ${cycle.merchant_name || 'Unknown Merchant'} (Case: ${cycle.case_number || cycle.case_no || 'Unknown'}, Amount: ${cycle.currency || 'USD'} ${cycle.amount || '0.00'})`
            } else {
                // Subsequent cycles - context-aware creation message based on case type
                const caseType = cycle.type || 'Case'
                if (caseType.toLowerCase().includes('merchant response')) {
                    // merchant response = Merchant escalated the case to pre-arbitration or send back a second presentment as response
                    cycleAction = `Case created - ${caseType} received from merchant (Case: ${cycle.case_number || cycle.case_no || 'Unknown'})`
                } else if (caseType.toLowerCase().includes('issuer response')) {
                    // issuer response = Issuer escalated the case to pre-arbitration  
                    cycleAction = `${caseType} initiated - Case escalated to pre-arbitration (Case: ${cycle.case_number || cycle.case_no || 'Unknown'})`
                } else {
                    // Other case types - generic creation
                    cycleAction = `Case created - ${caseType} created (Case: ${cycle.case_number || cycle.case_no || 'Unknown'})`
                }
            }
            
            // For initial case creation (first chronologically), use a user; for escalated cases, use System
            // Exception: PreArb In cases are always System since they're received from merchants
            const isMerchantResponseCase = cycle.type && cycle.type.toLowerCase().includes('merchant response')
            const caseCreationUser = (cycleIndex === 0 && !isMerchantResponseCase) ? 'Dalila Pelaez' : 'System'
            
            records.push({
                timestamp: formatDate(creationDate, 2000 + cycleIndex * 100),
                action: cycleAction,
                user: caseCreationUser
            })
            
            // Add intermediate workflow steps for all cycles (standard case processing)
            const caseNumber = cycle.case_number || cycle.case_no || 'Unknown'
            
            // Intermediate workflow step (In Progress - Pending Response)
            // Skip for PreArb In and Upload Pending cases since "Pending Response" doesn't make business sense
            // const isPreArbIn = cycle.type && cycle.type.toLowerCase().includes('merchant response')
            // const isUploadPending = cycle.case_status && cycle.case_status.toLowerCase().includes('upload pending')
            // if (!isPreArbIn && !isUploadPending) {
            //     records.push({
            //         timestamp: formatDate(creationDate, 2500 + cycleIndex * 100),
            //         action: `Case status updated to In Progress - Pending Response (Case: ${caseNumber})`,
            //         user: 'System'
            //     })
            // }
            
            // Status change when response is received (few days after creation)
            const responseDate = addDays(creationDate, 3)
            const hasEscalationNote = cycle.case_note && cycle.case_note.toLowerCase().includes('escalated')
            
            if (hasEscalationNote) {
                // For escalated cases: Review Pending - Response Received
                records.push({
                    timestamp: formatDate(responseDate, 2200),
                    action: `Case status updated to Review Pending - Response Received (Case: ${caseNumber})`,
                    user: 'System'
                })
            } else if (cycle.case_status && cycle.case_status.toLowerCase() === 'rejection pending') {
                // Only add Rejection Pending if that's the actual case status
                records.push({
                    timestamp: formatDate(responseDate, 2200),
                    action: `Case status updated to Rejection Pending (Case: ${caseNumber})`,
                    user: 'System'
                })
            }
            
            // Case note related actions
            if (cycle.case_note) {
                let noteAction = ''
                let noteUser = 'System'
                let noteDate = addDays(creationDate, 1)
                const noteCaseNumber = cycle.case_number || cycle.case_no || 'Unknown'
                
                // Check if this is an escalation-related note that should happen on the due date
                const isEscalationNote = cycle.case_note.toLowerCase().includes('escalated')
                if (isEscalationNote && cycle.due_date) {
                    // For escalation notes, use the due date (when agent works the case)
                    noteDate = parseDate(cycle.due_date)
                } else if (isEscalationNote) {
                    // Fallback: same day as creation if no due date
                    noteDate = addDays(creationDate, 0)
                }
                
                switch (cycle.case_note.toLowerCase()) {
                    case 'save for later':
                        noteAction = `Save for later (Case: ${noteCaseNumber})`
                        noteUser = 'Nick Reinhart'
                        break
                    // case 'escalated to pre-arb':
                    //     // Check if this escalation leads to PreArb Out (issuer) or PreArb In (merchant)
                    //     const hasPreArbOut = allCycles.some(c => c.type && c.type.toLowerCase().includes('issuer response'))
                    //     if (hasPreArbOut || (cycle.type && cycle.type.toLowerCase().includes('issuer response'))) {
                    //         // This escalation leads to or is part of PreArb Out - issuer escalated
                    //         noteAction = `Case escalated to Pre-Arbitration (Case: ${noteCaseNumber})`
                    //         noteUser = 'Nick Reinhart'
                    //     } else {
                    //         // This escalation leads to PreArb In - merchant escalated
                    //         noteAction = `Case escalated to Pre-Arbitration by merchant (Case: ${noteCaseNumber})`
                    //         noteUser = 'System'
                    //     }
                    //     break
                    case 'escalated to chargeback':
                        noteAction = `Escalated to Chargeback (Case: ${noteCaseNumber})`
                        noteUser = 'Ryan Sauer'
                        break
                    case 'missing document':
                    case 'prearb in received':
                    case 'prearb received':
                    case 'review response':
                    case 'response received':
                    case 'submission error':
                        // Skip - not needed
                        noteAction = ''
                        break
                    default:
                        noteAction = `${cycle.case_note} (Case: ${noteCaseNumber})`
                        noteUser = 'Nick Reinhart'
                        break
                }
                
                // Only add the record if noteAction was set
                if (noteAction) {
                    // For escalation notes, use earlier timestamp to ensure they appear before case creation/closure
                    const timestampBase = isEscalationNote ? 1000 : 3000
                    records.push({
                        timestamp: formatDate(noteDate, timestampBase + cycleIndex * 100),
                        action: noteAction,
                        user: noteUser
                    })
                }
            }
            
            // Status change records
            if (cycle.case_status) {
                let statusAction = ''
                const statusUser = 'System' // All status changes are system changes
                let statusDate = addDays(creationDate, 1)
                const caseNumber = cycle.case_number || cycle.case_no || 'Unknown'
                
                // Check if this case was closed due to escalation
                const isEscalationClosure = cycle.case_status.toLowerCase() === 'closed' && 
                    cycle.case_note?.toLowerCase().includes('escalated')
                
                if (isEscalationClosure && cycle.due_date) {
                    // For escalation closures, use the due date (when agent worked the case and escalated)
                    statusDate = parseDate(cycle.due_date)
                } else if (isEscalationClosure) {
                    // Fallback: use creation date if no due date
                    statusDate = addDays(creationDate, 0)
                }
                
                switch (cycle.case_status.toLowerCase()) {
                    case 'review pending':
                        statusAction = `Case status updated to Review Pending (Case: ${caseNumber})`
                        break
                    case 'rejection pending':
                        // Skip - already handled in intermediate workflow steps
                        statusAction = ''
                        break
                    case 'closed':
                        statusAction = `Case closed - Response Received (Case: ${caseNumber})`
                        if (!isEscalationClosure) {
                            statusDate = completedDate || (dueDate ? addDays(dueDate, -1) : addDays(creationDate, 7))
                        }
                        break
                    case 'case created':
                         statusAction = ''
                        break
                    default:
                        statusAction = `Case status updated to ${cycle.case_status} (Case: ${caseNumber})`
                        break
                }
                
                // Only add the record if statusAction was set
                if (statusAction) {
                    // For escalation closures, use timestamp that comes just after the escalation note (within seconds)
                    const timestampBase = isEscalationClosure ? 1100 : 4000
                    records.push({
                        timestamp: formatDate(statusDate, timestampBase + cycleIndex * 100),
                        action: statusAction,
                        user: statusUser
                    })
                }
            }
            
            // Due date reminders removed per user request
            
            // Type-specific actions
            if (cycle.type) {
                const typeAction = cycle.type.toLowerCase()
                if (typeAction.includes('representment')) {
                    records.push({
                        timestamp: formatDate(addDays(creationDate, 3), 6000 + cycleIndex * 100),
                        action: 'Representment evidence compiled - Supporting documents gathered and reviewed',
                        user: 'Ryan Sauer'
                    })
                    
                    records.push({
                        timestamp: formatDate(addDays(creationDate, 5), 6100 + cycleIndex * 100),
                        action: 'Representment submitted to card network - Awaiting issuer response',
                        user: 'System'
                    })
                }
            }
        })
        
        // Sort by timestamp (newest first)
        return records.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    }

    return (
        <>
            <Tooltip arrow placement="top" title={tooltipTitle}>
                <IconButton
                    className={className}
                    onClick={handleOpenHistory}
                    size={iconSize}
                >
                    <Icon
                        className={`fa fa-history`}
                        id={`${testId}-history-icon-${row.id}`}
                    />
                </IconButton>
            </Tooltip>
            {anchorEl && (
                <CaseHistoryPopover
                    anchorEl={anchorEl}
                    onClose={handleCloseHistory}
                    caseInfo={caseInfoWithMockData}
                    testId={`${testId}-history-popover`}
                />
            )}
        </>
    )
}

export default CaseHistory
